import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { HubManagementDialogContentLayout } from "../../HubManagementDialogContentLayout";
import {
	DialogBackdropState,
	Form,
	FormItem,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import {
	HubManagementSettingsViewModel,
	saveHubSettings,
	DocumentLocationViewModel,
	DocumentLocationModelKind,
	DocumentLocationSaveModel,
	HubManagementSettingsSaveModel,
	getHubSettings
} from "client/Api/Hub/hubSettings";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import { DocumentLocationSelector } from "./DocumentLocationSelector";
import { never } from "client_legacy/ndaw/errors";
import { PathRedirect } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import {
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation
} from "client/Validation";
import { isNotNil } from "client/Misc/utils";
import { useSelectDocumentsFolderDialog } from "client/Features/Documents/Dialogs/SelectDocumentsFolderDialog";

export type HubSettingsProps = {
	isMobile: boolean;
	isResponsive: boolean;
	onParentBackdropChange: (backdrop: DialogBackdropState) => void;
};

export const HubSettings = (props: HubSettingsProps): ReactElement => {
	const {
		isMobile,
		isResponsive,
		onParentBackdropChange
	} = props;

	const [initialModel, setInitialModel] = useState<HubSettingsFormData>(initialFormModel);
	const [formModel, setFormModel] = useState<HubSettingsFormData>(initialFormModel);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [state, setState] = useState<State>({
		initialDefaultImageLocation: null
	});

	const [selectDocumentsFolderDialog, openSelectDocumentsFolderDialog] = useSelectDocumentsFolderDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<HubSettingsFormData> = useFormHandler(
		formModel,
		setFormModel,
		"NoValidation"
	);

	const validation: IRootValidation<HubSettingsFormData> = formHandler.validation;

	useEffect(
		(): (() => void) => {
			async function fetchData(signal: AbortSignal) {
				try {
					setIsBusy(true);
					const result: HubManagementSettingsViewModel = await getHubSettings(signal);

					setInitialModel(initialFormModel);
					setFormModel(initialFormModel);

					const { defaultImageLocation } = result;

					setState({
						initialDefaultImageLocation: defaultImageLocation
							? convertToLocationFormModel(defaultImageLocation)
							: null
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const cancelHandler = useCallback(
		(): void => {
			setFormModel(initialModel);
			validation.reset();
		},
		[
			initialModel,
			validation
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const saveModel: HubManagementSettingsSaveModel = convertToSaveModel(formModel);
				await saveHubSettings(saveModel, globalCancellation);
				setInitialModel(initialFormModel);

				const { defaultImageLocation } = formModel;

				if (defaultImageLocation.isSet) {
					setState({
						initialDefaultImageLocation: defaultImageLocation.value
					});
				}

				setIsBusy(false);
				alertManager.showSuccess(localize("_ChangesSaved"));
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e, serverPathRedirects)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			validation,
			formModel,
			globalCancellation,
			isBusy
		]
	);

	const defaultActions = useMemo(
		(): readonly SettingsAction[] => getDefaultActions({
			save: saveHandler,
			cancel: cancelHandler
		}),
		[saveHandler, cancelHandler]
	);

	const actions = useMemo(
		(): readonly SettingsAction[] => buildActionsForTopBar<undefined, SettingsAction>(
			{ defaultActions },
			false,
			isResponsive,
			false
		),
		[defaultActions, isResponsive]
	);

	return (
		<>
			<HubManagementDialogContentLayout
				trackingId="settings-overview"
				isMobile={isMobile}
				isResponsive={isResponsive}
				title={localize("_Settings")}
				actionContext={undefined}
				actions={actions}
				isBusy={isBusy}
			>
				<Form onSubmit="NONE">
					<FormItem label={localize("_DefaultImageFolder")}>
						<DocumentLocationSelector
							attachable={formHandler.for("defaultImageLocation")}
							onIsBusyChange={setIsBusy}
							alertManager={alertManager}
							onOpenSelectFolderDialog={openSelectDocumentsFolderDialog}
							initialDefaultImageLocation={state.initialDefaultImageLocation}
							onParentBackdropChange={onParentBackdropChange}
						/>
						<ValidationError {...formHandler.for("defaultImageLocation").attachToValidationError()}/>
					</FormItem>
				</Form>
			</HubManagementDialogContentLayout>
			{selectDocumentsFolderDialog}
		</>
	);
};
HubSettings.displayName = "HubSettings";

const serverPathRedirects: readonly PathRedirect[] = [
	{
		pattern: new DotPathWildcardPattern("DefaultImageLocation.**"),
		replacement: "DefaultImageLocation"
	}
];

type SettingsAction = ToolbarAction<undefined>;

type ActionHandlers = {
	save: () => void;
	cancel: () => void;
};

function getDefaultActions(
	handlers: ActionHandlers
): readonly SettingsAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			isPrimary: true,
			canExecute: (): boolean => true,
			execute: (): void => handlers.save()
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: (): boolean => true,
			execute: (): void => handlers.cancel()
		}
	];
}

export type DocumentLocationFormData = DocumentLocationSaveModel & {
	displayName: string;
};

export type HubSettingsFormData = OverrideProperties<
	HubManagementSettingsSaveModel,
	{
		defaultImageLocation: OptionalModel<DocumentLocationFormData | null>;
	}
>;

const initialFormModel: HubSettingsFormData = {
	defaultImageLocation: { isSet: false }
};

type State = {
	initialDefaultImageLocation: DocumentLocationFormData | null;
};

function convertToLocationFormModel(
	model: DocumentLocationViewModel
): DocumentLocationFormData {
	switch (model.kind) {
		case DocumentLocationModelKind.SharePoint:
			return {
				kind: DocumentLocationModelKind.SharePoint,
				displayName: model.displayName,
				itemId: model.itemId,
				listLocation: model.listLocation,
			};
		case DocumentLocationModelKind.WebDav:
			return {
				kind: DocumentLocationModelKind.WebDav,
				displayName: model.displayName,
				fileId: model.fileId,
				serverId: model.serverId
			};
		default:
			never(model);
	}
}

function convertToSaveModel(
	model: HubSettingsFormData
): HubManagementSettingsSaveModel {
	const {
		defaultImageLocation
	} = model;

	return {
		defaultImageLocation: defaultImageLocation.isSet
			? {
				isSet: true,
				value: isNotNil(defaultImageLocation.value)
					? convertToLocationSaveModel(defaultImageLocation.value)
					: null
			}
			: { isSet: false }
	};
}

function convertToLocationSaveModel(
	model: DocumentLocationFormData
): DocumentLocationSaveModel {
	switch (model.kind) {
		case DocumentLocationModelKind.SharePoint:
			return {
				kind: DocumentLocationModelKind.SharePoint,
				itemId: model.itemId,
				listLocation: model.listLocation,
			};
		case DocumentLocationModelKind.WebDav:
			return {
				kind: DocumentLocationModelKind.WebDav,
				fileId: model.fileId,
				serverId: model.serverId
			};
		default:
			never(model);
	}
}